import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { EDataTable } from "../../../components/EDataTable";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { useNavigate } from "react-router-dom";

import { OverlayPanel } from "primereact/overlaypanel";
import {
  Activity,
  Clock,
  Wand2,
  Undo2,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  UserCheck,
  Package,
  Loader2,
  PauseCircle,
  Truck,
  Eye,
  UserPlus,
  Filter as FilterIcon,
  RotateCcw,
  RefreshCw,
  Icon,
  CheckLineIcon,
  CheckCheck,
  Pencil,
  Unlock,
  Lock,
  X,
  Copy,
  ChevronDown,
  AlertCircle,
  Share2,
  Phone,
} from "lucide-react"; // Lucide ikonları
import { toast } from "react-toastify";

// Bileşenler ve Tipler
import { DataTableColumn, Filter } from "../../../types";
import {
  IconButton,
  IconTextButton,
  TextButton,
} from "../../../components/ButtonComponents";
import { SummaryCard } from "../../../components/SummaryCard";
import { TableActions } from "../../../components/TableActions";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { WarehouseTask, WarehouseTaskAction } from "../../../types/warehouse";
import { useWarehouseTaskStore } from "../../../store/warehouseTask";
import { WarehouseTaskDetailDialog } from "./WarehouseTaskDetailDialog";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { DateRangePicker } from "../../../components/DateRangePicker";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { usePreferenceStore } from "../../../store/preferenceStore";
import { useUIStore } from "../../../store/uiStore";
import { useAuthStore } from "../../../store/authStore";
import { tenantConfig } from "../../../config/tenantConfig";
import { WarehouseTaskPrintDialog } from "./WarehouseTaskPrintDialog";
import { Printer as PrinterIcon, Database } from "lucide-react";
import { ManualOrderSyncModal } from "../../../components/ManualOrderSyncModal";

interface ExtendedWarehouseTask extends WarehouseTask {
  actions: WarehouseTaskAction[];
}
// 🎯 Kargo seçeneği tipi tanımı
interface CargoOption {
  label: string;
  value: string;
}

const CARGO_OPTIONS = [
  { label: "YURTİÇİ", value: "YURTİÇİ" },
  { label: "ARAS", value: "ARAS" },
  { label: "MNG", value: "MNG" },
  { label: "PTT", value: "PTT" },
  { label: "KOLAY GELSİN", value: "KOLAY GELSİN" },
  { label: "DEPO TESLİM", value: "DEPO TESLİM" },
  { label: "GEL-AL", value: "GEL-AL" },
  { label: "-", value: "-" },
];

const COLOR_CONFIG: Record<string, { text: string; bg: string }> = {
  YURTİÇİ: {
    text: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-500",
  },
  ARAS: { text: "text-red-600 dark:text-red-400", bg: "bg-red-500" },
  MNG: { text: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500" },
  PTT: { text: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500" },
  "KOLAY GELSİN": {
    text: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-500",
  },
  "DEPO TESLİM": {
    text: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500",
  },
  "GEL-AL": {
    text: "text-surface-600 dark:text-surface-400",
    bg: "bg-surface-400",
  },
  "GEL AL": {
    text: "text-surface-600 dark:text-surface-400",
    bg: "bg-surface-400",
  },
  "-": { text: "text-surface-600 dark:text-surface-400", bg: "bg-surface-400" },
};

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: any; colorClass: string }
> = {
  PENDING_PICKING: {
    label: "Bekliyor",
    icon: Clock,
    colorClass: "bg-orange-500 text-white",
  },
  PICKING: {
    label: "Toplanıyor",
    icon: Loader2,
    colorClass: "bg-blue-500 text-white",
  },
  PICKING_PAUSED: {
    label: "Toplama Duraklatıldı",
    icon: PauseCircle,
    colorClass: "bg-amber-500 text-white",
  },
  PACKAGING_PAUSED: {
    label: "Paketleme Duraklatıldı",
    icon: PauseCircle,
    colorClass: "bg-amber-500 text-white",
  },
  PAUSED: {
    label: "Duraklatıldı",
    icon: PauseCircle,
    colorClass: "bg-amber-500 text-white",
  },
  SHORTAGE: {
    label: "Eksik Bildirildi",
    icon: AlertTriangle,
    colorClass: "bg-red-500 text-white",
  },
  REVISION_NEEDED: {
    label: "Revizyon Bekliyor",
    icon: AlertCircle,
    colorClass: "bg-amber-500 text-white",
  },
  PENDING_PACKAGING: {
    label: "Paketleme Bekliyor",
    icon: Package,
    colorClass: "bg-orange-500 text-white",
  },
  PACKAGING: {
    label: "Paketleniyor",
    icon: Loader2,
    colorClass: "bg-indigo-500 text-white",
  },
  COMPLETED: {
    label: "Tamamlandı",
    icon: CheckCircle2,
    colorClass: "bg-green-500 text-white",
  },
  CANCELLED: {
    label: "İptal",
    icon: Trash2,
    colorClass: "bg-rose-500 text-white",
  },
};

// --- CUSTOM STATUS DROPDOWN BİLEŞENİ ---
const CustomStatusDropdown = ({
  currentStatus,
  onSave,
  disabled
}: {
  currentStatus: string;
  onSave: (val: string) => void;
  disabled?: boolean;
}) => {
  const item = STATUS_CONFIG[currentStatus] || {
    label: currentStatus,
    colorClass: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    icon: Activity,
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div
          className={`w-full  ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:brightness-110 transition-all"}`}
        >
          <div className={`w-full flex items-center justify-between rounded-md px-2 py-3 ${item.colorClass}`}>
            <div className="flex items-center gap-1.5">
              <item.icon size={12} />
              <span className="text-[10px] uppercase font-bold text-left leading-none">
                {item.label}
              </span>
            </div>
            {!disabled && <ChevronDown size={14} className="opacity-50 ml-1 shrink-0" />}
          </div>
        </div>
      </DropdownMenu.Trigger>

      {!disabled && (
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            className="min-w-[180px] max-h-[300px] overflow-y-auto custom-scrollbar bg-card border border-borderColor rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.15)] p-1 z-[9999] text-text select-none focus:outline-none data-[state=open]:animate-modalIn data-[state=closed]:animate-modalOut"
          >
            <DropdownMenu.Label className="text-[10px] font-black uppercase tracking-widest text-text/40 px-3 py-2">
              Durum Seçin
            </DropdownMenu.Label>

            {Object.entries(STATUS_CONFIG).map(([key, configItem]) => (
              <DropdownMenu.Item
                key={key}
                onSelect={(e) => {
                  e.preventDefault();
                  if (key !== currentStatus) {
                    onSave(key);
                  }
                }}
                className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer focus:outline-none select-none rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${key === currentStatus ? 'bg-primary/5' : ''}`}
              >
                <Tag className={`w-full !justify-start ${configItem.colorClass} border-none`}>
                  <div className="flex items-center gap-1.5 px-2 py-1">
                    <configItem.icon size={12} />
                    <span className="text-[10px] uppercase font-bold text-left">
                      {configItem.label}
                    </span>
                  </div>
                </Tag>
                {key === currentStatus && (
                  <Tag className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-none ml-1 shrink-0 px-2">
                    <div className="flex items-center justify-center py-1">
                      <CheckLineIcon size={14} strokeWidth={3} />
                    </div>
                  </Tag>
                )}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </DropdownMenu.Root>
  );
};

// --- CUSTOM CARGO DROPDOWN BİLEŞENİ ---
const CustomCargoDropdown = ({
  currentCargo,
  onSave,
}: {
  currentCargo: string;
  onSave: (val: string) => void;
}) => {
  const { getShipmentFirms } = useWarehouseTaskStore();
  const [isOpen, setIsOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number | "auto";
    bottom: number | "auto";
    left: number;
    width: number;
    transformOrigin: string;
  }>({ top: 0, bottom: "auto", left: 0, width: 0, transformOrigin: "top left" });

  const updatePosition = useCallback(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const menuHeight = 260; // Tahmini max menü yüksekliği
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number | "auto" = rect.bottom + 4;
      let bottom: number | "auto" = "auto";
      let transformOrigin = "top left";

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        // Aşağıda yeterli alan yoksa ve yukarıda daha çok alan varsa yukarı aç
        top = "auto";
        bottom = window.innerHeight - rect.top + 4;
        transformOrigin = "bottom left";
      }

      setPosition({
        top,
        bottom,
        left: rect.left,
        width: Math.max(rect.width, 220),
        transformOrigin
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = (e: Event) => {
      // Eğer scroll menü içinde (liste) kaydırılıyorsa kapanmasını veya hareketini engelle
      if (menuRef.current && menuRef.current.contains(e.target as Node)) {
        return;
      }
      // Dışarıda kaydırma varsa menü butonu takip etsin
      updatePosition();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true); // true = capture phase
      window.addEventListener("resize", updatePosition);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  const { cargoHistory, addCargoToHistory, removeCargoFromHistory } = useUIStore();

  const handleSelect = (val: string) => {
    onSave(val);
    addCargoToHistory(val);
    setIsOpen(false);
  };

  const [apiCargos, setApiCargos] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      getShipmentFirms()
        .then((data: any) => {
          if (Array.isArray(data)) setApiCargos(data);
        })
        .catch((err: any) => console.error("Kargolar yüklenemedi", err));
    }
  }, [isOpen, getShipmentFirms]);

  const allCargoOptions = useMemo(() => {
    const apiOptions = apiCargos.map((m: string) => ({ label: m, value: m }));

    const combined = [...apiOptions, ...CARGO_OPTIONS];
    const uniqueMap = new Map(combined.map(item => [item.value, item]));

    // Geçmişteki kargoları en başa al (en son seçilen en üstte olacak şekilde)
    const sortedOptions: CargoOption[] = [];

    // 1. Önce geçmişten gelenleri sırasıyla ekle
    cargoHistory.forEach(hist => {
      if (uniqueMap.has(hist)) {
        sortedOptions.push(uniqueMap.get(hist)!);
        uniqueMap.delete(hist);
      } else {
        sortedOptions.push({ label: hist, value: hist });
      }
    });

    // 2. Kalanları ekle
    uniqueMap.forEach(item => {
      sortedOptions.push(item);
    });

    return sortedOptions;
  }, [apiCargos, cargoHistory]);

  const isCustomCargo = useCallback((val: string) => {
    return !apiCargos.includes(val) && !CARGO_OPTIONS.some(c => c.value === val);
  }, [apiCargos]);

  const filteredOptions = useMemo(() => {
    if (!manualValue.trim()) return allCargoOptions;
    const search = manualValue.trim().toLowerCase();
    return allCargoOptions.filter(opt => opt.label.toLowerCase().includes(search));
  }, [allCargoOptions, manualValue]);

  const handleManualSubmit = () => {
    if (manualValue.trim()) {
      const searchVal = manualValue.trim().toUpperCase();

      let val = searchVal;

      // 1. Önce tam eşleşen var mı kontrol et
      const exactMatch = allCargoOptions.find(opt => opt.label.toUpperCase() === searchVal);

      if (exactMatch) {
        val = exactMatch.value;
      }
      // 2. Tam eşleşme yok ama filtrelenen listede TEK BİR kayıt kaldıysa onu seç (örn: 'depo' -> 'DEPO TESLİM')
      else if (filteredOptions.length === 1) {
        val = filteredOptions[0].value;
      }

      onSave(val);
      addCargoToHistory(val);
      setIsOpen(false);
      setManualValue("");
    }
  };

  const shipmentText = (currentCargo || "-").trim().toUpperCase();
  const config = COLOR_CONFIG[shipmentText] || {
    text: "text-primary-600 dark:text-primary-400",
    bg: "bg-primary-500",
  };

  return (
    <>
      <div
        ref={dropdownRef}
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-bold ${config.bg} bg-opacity-15 dark:bg-opacity-20 ${config.text} border border-borderColor w-fit cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={(e) => {
          e.stopPropagation();
          if (!isOpen) {
            updatePosition();
          }
          setIsOpen(!isOpen);
        }}
        title="Kargo Firmasını Değiştir"
      >
        {shipmentText}
        <Pencil size={10} className="ml-1 opacity-70" />
      </div>

      {isOpen && typeof window !== "undefined" && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999] bg-card border border-borderColor rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          style={{
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            minWidth: position.width,
            transformOrigin: position.transformOrigin,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-borderColor bg-inputBg">
            <div className="flex gap-2">
              <input
                type="text"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                placeholder="Kargo Ara veya Ekle..."
                className="flex-1 w-full bg-transparent border border-inputBorder rounded-lg px-2 py-1.5 text-xs text-inputText focus:outline-none focus:border-primary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleManualSubmit();
                  }
                }}
                autoFocus
              />
              <button
                onClick={handleManualSubmit}
                className="bg-primary text-white px-2 rounded-lg hover:bg-primary/90 flex items-center justify-center transition-colors"
                title="Kaydet"
              >
                <CheckLineIcon size={14} />
              </button>
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1 py-1.5 flex flex-col gap-0.5">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isCustom = isCustomCargo(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`flex items-center justify-between px-3 py-1.5 text-[13px] font-medium cursor-pointer rounded-lg transition-colors group ${opt.value === shipmentText
                      ? "bg-primary/20 text-primary font-bold"
                      : "text-text hover:bg-primary/10 hover:text-primary"
                      }`}
                  >
                    <span className="flex-1">{opt.label}</span>
                    {isCustom && (
                      <button
                        className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCargoFromHistory(opt.value);
                        }}
                        title="Geçmişten Sil"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="px-3 py-2 text-xs text-text/50 italic text-center">
                Eşleşen kargo bulunamadı. Eklemek için Enter'a basın.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export const WarehouseMonitor = () => {
  const taskStore = useWarehouseTaskStore();
  const staffSelectDialog = useSelectDialog<any>();
  const filterOp = useRef<OverlayPanel>(null);
  const confirmModal = useConfirmModal();
  const isSummaryFetched = useRef(false);
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  // --- State ---
  const [selectedTask, setSelectedTask] = useState<WarehouseTask | null>(null);
  // const [summary, setSummary] = useState({
  //     POOL: 0, ASSIGNED: 0, PROGRESS: 0,
  //     COMPLETED: 0, SHORTAGE: 0, CANCELLED: 0
  // });

  // Filtre State'leri
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterPriorities, setFilterPriorities] = useState<number[]>([]);
  const [filterUsers, setFilterUsers] = useState<number[]>([]); // 🌟 Yeni: Personel filtresi
  const [filterSources, setFilterSources] = useState<string[]>([]); // 🌟 Sipariş Kaynağı (order.source) filtresi
  const [filterMyAssignedOnly, setFilterMyAssignedOnly] = useState(false); // 🌟 "Atadıklarım" aç/kapa filtresi
  const [apiSources, setApiSources] = useState<{ label: string; value: string }[]>([]); // 🌟 API'den gelen kaynaklar
  const [staffOptions, setStaffOptions] = useState<any[]>([]); // 🌟 Yeni: Personel seçenekleri
  const [filterDates, setFilterDates] = useState<(Date | null)[] | null>(null);
  // Tarih State (Önceki düzeltmemize uygun dizi formatında)
  const [editingTask, setEditingTask] = useState<WarehouseTask | null>(null);
  const dateOp = useRef<OverlayPanel>(null);
  const noteOp = useRef<any>(null);
  const addressOp = useRef<any>(null);
  const phoneOp = useRef<any>(null);

  const [tempNote, setTempNote] = useState("");
  // Süreç ve Personel State'leri
  const [filterAssignedUsers, setFilterAssignedUsers] = useState<string[]>([]);
  const [filterPickers, setFilterPickers] = useState<string[]>([]);
  const [filterPackers, setFilterPackers] = useState<string[]>([]);
  const [addressEditType, setAddressEditType] = useState<
    "shipping" | "billing" | null
  >(null);
  const [tempAddressLine, setTempAddressLine] = useState<string>("");
  const [tempPhone, setTempPhone] = useState("");
  const [selectedTasks, setSelectedTasks] = useState<WarehouseTask[]>([]);
  const [isManualSyncOpen, setIsManualSyncOpen] = useState(false);

  // 🌟 Print State
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [taskToPrint, setTaskToPrint] = useState<WarehouseTask | null>(null);
  const [printCategory, setPrintCategory] = useState<"PICKING" | "PACKING">("PICKING");

  const sourceOptions = useMemo(() => {
    const map = new Map<string, { label: string; value: string }>();

    // 1. API'den gelen dinamik kaynaklar (/warehouse-task/sources veya /order/sources)
    apiSources.forEach((opt) => {
      if (opt && opt.value) {
        map.set(opt.value, opt);
      }
    });

    // 2. Halihazırda store tablosundaki verilerden gelen kaynaklar (varsa)
    (taskStore.data || []).forEach((t) => {
      const src = t.order?.source;
      if (src && typeof src === "string" && src.trim().length > 0 && !map.has(src)) {
        map.set(src, { label: src, value: src });
      }
    });

    return Array.from(map.values());
  }, [apiSources, taskStore.data]);

  const extraFilters = useMemo(() => {
    const filters: Filter[] = [];

    // 1. Durum Filtresi (IN Operatörü)
    if (filterStatuses.length > 0) {
      filters.push({
        field: "status",
        operator: "in" as any,
        value: filterStatuses,
      });
    }

    // 2. Öncelik Filtresi (IN Operatörü)
    if (filterPriorities.length > 0) {
      filters.push({
        field: "priority",
        operator: "in" as any,
        value: filterPriorities,
      });
    }

    // 3. Sipariş Kaynağı Filtresi (order.source) (IN Operatörü)
    if (filterSources.length > 0) {
      filters.push({
        field: "order.source",
        operator: "in" as any,
        value: filterSources,
      });
    }

    // --- BİRLEŞTİRİLMİŞ PERSONEL FİLTRESİ ---
    // Sizin kullandığınız filterAssignedUsers state'i ile kontrol ediyoruz
    if (filterAssignedUsers && filterAssignedUsers.length > 0) {
      filters.push({
        logic: "or", // Atanan VEYA Operasyonel aksiyon alan personel mantığı
        filters: [
          {
            // Koşul A: Doğrudan göreve atanan sorumlu personel olması durumu
            field: "assignedUserId",
            operator: "in" as any,
            value: filterAssignedUsers,
          },
          {
            // Koşul B: Toplama veya Paketleme operasyonel aksiyonlarından birini yapmış olması durumu (Genel ADMIN_UPDATE hariç)
            logic: "and",
            filters: [
              {
                field: "actions.some.userId",
                operator: "in" as any,
                value: filterAssignedUsers,
              },
              {
                field: "actions.some.actionType",
                operator: "in" as any,
                value: [
                  "PICKING_START",
                  "PICKING_DONE",
                  "ITEM_PICKED",
                  "PACKAGING_START",
                  "PACKAGING_DONE",
                  "SHORTAGE_REPORTED",
                ],
              },
            ],
          } as any,
        ],
      } as any);
    }
    // ---------------------------------------------

    // 4. "Atadıklarım" Filtresi (Yönetici olarak personele atama yaptığım veya bana atanan işler)
    if (filterMyAssignedOnly) {
      const myId = currentUser?.id;
      if (myId) {
        filters.push({
          logic: "or",
          filters: [
            // Koşul A: Giriş yapan kullanıcının yaptığı personel atama logu ("Personel:" içeren ADMIN_UPDATE logu)
            {
              logic: "and",
              filters: [
                {
                  field: "actions.some.userId",
                  operator: "eq" as any,
                  value: myId,
                },
                {
                  field: "actions.some.description",
                  operator: "contains" as any,
                  value: "Personel:",
                },
              ],
            } as any,
            // Koşul B: Veya doğrudan kullanıcının kendi üstüne atanmış olan işler
            {
              field: "assignedUserId",
              operator: "eq" as any,
              value: myId,
            },
          ],
        } as any);
      } else {
        filters.push({
          field: "assignedUserId",
          operator: "neq" as any,
          value: null,
        });
      }
    }

    // 5. Tarih Aralığı Filtresi (BaseController'a Uygun)
    if (
      filterDates &&
      Array.isArray(filterDates) &&
      filterDates[0] &&
      filterDates[1]
    ) {
      const startDate = new Date(filterDates[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(filterDates[1]);
      endDate.setHours(23, 59, 59, 999);

      filters.push({
        logic: "and",
        filters: [
          {
            field: "createdAt",
            operator: "gte",
            value: startDate.toISOString(),
          },
          { field: "createdAt", operator: "lte", value: endDate.toISOString() },
        ],
      } as any);
    }

    return filters;
  }, [
    filterStatuses,
    filterPriorities,
    filterSources,
    filterAssignedUsers,
    filterMyAssignedOnly,
    filterDates,
    currentUser?.id,
  ]);

  useEffect(() => {
    taskStore.getStaffList().then((res) => {
      setStaffOptions(res);
    });
    taskStore.getOrderSources().then((sources) => {
      if (sources && sources.length > 0) {
        setApiSources(sources);
      }
    });
  }, []);

  // 1. refreshAll fonksiyonunu güncelle (Sadece manuel yenilemelerde ve action sonralarında tetiklenir)
  // WarehouseMonitor.jsx içindeki refreshAll
  const refreshAll = useCallback(
    async (silent = false) => {
      let loadToast;
      if (!silent) loadToast = toast.loading("Veriler güncelleniyor...");
      try {
        // Sayfayı 1'e zorlamayın, mevcut queryParams neyse onu tazelesin
        await taskStore.fetchDataDynamic({ silent });

        if (!silent && loadToast) {
          toast.update(loadToast, {
            render: "Veriler tazelendi.",
            type: "success",
            isLoading: false,
            autoClose: 1500,
          });
        }
      } catch (error) {
        if (loadToast) toast.dismiss(loadToast);
      }
    },
    [taskStore],
  );

  // SSE (Server-Sent Events) for real-time updates
  useEffect(() => {
    const token = useAuthStore.getState().accessToken;
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

    const eventSource = new EventSource(`${apiUrl.replace(/\/$/, '')}/sse/warehouse-sync?token=${token}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Refresh silently when receiving a sync event
        if (event.type === 'REFRESH_WAREHOUSE' || data.action === 'syncOrders') {
          refreshAll(true);
        }
      } catch (e) {
        console.error("SSE Parse Error:", e);
      }
    };

    eventSource.addEventListener("REFRESH_WAREHOUSE", (event) => {
      refreshAll(true);
    });

    eventSource.onerror = (error) => {
      console.error("SSE Connection Error:", error);
      // EventSource automatically attempts to reconnect
    };

    return () => {
      eventSource.close();
    };
  }, [refreshAll]);
  // WarehouseMonitor.jsx içine ekleyin
  useEffect(() => {
    // Eğer daha önce çekildiyse (true ise) hiçbir şey yapmadan çık
    if (isSummaryFetched.current) return;

    // Çekilmediyse ref'i true yap ki bir daha buraya girmesin
    isSummaryFetched.current = true;

    //  taskStore.getSummary().then(setSummary).catch(console.error);
  }, []);

  // 3. handleTaskUpdate içindeki yenilemeyi yönet
  const handleTaskUpdate = useCallback(
    async (taskId: number, payload: any) => {
      try {
        await taskStore.adminUpdate(taskId, payload);
        toast.success("İşlem başarılı.");
        refreshAll(true); // Tüm veriyi sessizce tazeler
      } catch (e) {
        toast.error("Hata oluştu.");
      }
    },
    [taskStore, refreshAll],
  );

  const handleFinishTask = async (task: WarehouseTask) => {
    const confirmed = await confirmModal.confirm(
      `${task.order?.orderNumber} nolu siparişi operasyon sürecine girmeden "Tamamlandı" olarak işaretlemek istediğinize emin misiniz?`,
      "Hızlı Tamamla Onayı",
    );

    if (confirmed) {
      const loadToast = toast.loading("Görev tamamlanıyor...");
      try {
        await taskStore.finishPackaging(task.id);
        toast.update(loadToast, {
          render: "Görev başarıyla tamamlandı ve listeden düşürüldü.",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        refreshAll(true); // Hem özetleri hem tabloyu yenile
      } catch (e) {
        toast.update(loadToast, {
          render: "İşlem başarısız oldu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  // 💡 Görevi Sıfırla (Durumu başa çek ve personeli temizle)
  const handleResetTask = async (task: WarehouseTask) => {
    const confirmed = await confirmModal.confirm(
      `${task.order?.orderNumber} nolu siparişin depo sürecini tamamen sıfırlamak istediğinize emin misiniz? Tüm toplanan miktarlar ve aksiyonlar silinecektir.`,
      "Kritik İşlem Onayı",
    );

    if (confirmed) {
      const loadToast = toast.loading("Görev sıfırlanıyor...");
      try {
        await taskStore.resetTask(task.id);
        toast.update(loadToast, {
          render: "Görev başarıyla sıfırlandı ve havuza gönderildi.",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        refreshAll(true);
      } catch (e) {
        toast.update(loadToast, {
          render: "Sıfırlama başarısız oldu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const handleCopyCargoInfo = (r: any) => {
    const customerName = r.order?.account?.name || "Bilinmeyen Müşteri";
    const orderNo = r.order?.orderNumber || r.order?.id || "Belirtilmemiş";
    const cargoName = r.order?.shipment || "Belirtilmemiş";
    const trackingNo = r.order?.cargoTrackingNo;

    let statusStr = "Belirsiz";
    if (r.status === "PENDING_PICKING") statusStr = "⏳ Toplama Bekliyor";
    else if (r.status === "PICKING") statusStr = "📦 Toplanıyor";
    else if (r.status === "PENDING_PACKAGING") statusStr = "⏳ Paketleme Bekliyor";
    else if (r.status === "PACKAGING") statusStr = "📦 Paketleniyor";
    else if (r.status === "COMPLETED") statusStr = "🟢 Tamamlandı";
    else if (r.status === "SHORTAGE") statusStr = "🔴 Eksik Ürün Bildirildi";
    else if (r.status === "REVISION_NEEDED") statusStr = "🔴 Revizyon Bekliyor";
    else if (r.isDepo && r.customProgress >= 100) statusStr = "✅ Teslime Hazır";
    else if (!r.isDepo && r.customProgress >= 100) statusStr = "✅ Kargoya Hazır";

    const user = useAuthStore.getState().user;
    const firmName = user?.firmName || user?.firm?.name || tenantConfig.name;

    const isRealTracking = Boolean(
      trackingNo &&
      String(trackingNo).trim() !== String(r.order?.cargoIntegrationNo || '').trim() &&
      String(trackingNo).trim() !== String(r.order?.orderNumber || '').trim() &&
      String(trackingNo).trim() !== String(r.order?.id || '').trim()
    );

    let text = `🏢 *${firmName}*\n---------------------------------------------\n📦 *Sipariş Bilgisi - ${customerName}*\n---------------------------------------------\n🚚 *Kargo:* ${cargoName}\n🔖 *Sipariş No:* ${orderNo}\n📌 *Durum:* ${statusStr}`;

    if (isRealTracking) {
      text += `\n🔍 *Takip No:* ${trackingNo}`;
    } else if (trackingNo) {
      text += `\n🔍 *Takip No:* Kargoda Bekliyor`;
    }

    text += `\n---------------------------------------------\nPlasiyo - İşinize artı katar...`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success("Kargo bilgileri panoya kopyalandı, WhatsApp'a yapıştırabilirsiniz!");
    }).catch(() => {
      toast.error("Kopyalama başarısız oldu.");
    });
  };

  const handleDeleteTask = async (task: WarehouseTask) => {
    const confirmed = await confirmModal.confirm(
      `${task.order?.orderNumber} nolu siparişe ait depo görevini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      "Görevi Sil",
    );

    if (confirmed) {
      const loadToast = toast.loading("Görev siliniyor...");
      try {
        await taskStore.deleteTask(task.id);
        toast.update(loadToast, {
          render: "Görev başarıyla silindi.",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        refreshAll(true);
      } catch (e) {
        toast.update(loadToast, {
          render: "Görev silinirken bir hata oluştu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const openStaffSelector = async (task: ExtendedWarehouseTask) => {
    if (["COMPLETED", "CANCELLED"].includes(task.status)) {
      toast.warning(
        "Tamamlanmış veya iptal edilmiş bir işe personel atayamazsınız.",
      );
      return;
    }

    // 🌟 1. Store'dan jenerik sıralama ve kaydetme fonksiyonlarını alıyoruz
    const { getSortedList, saveLastSelection } = usePreferenceStore.getState();
    const MODULE_KEY = "warehouse_staff"; // Bu modüle özel benzersiz anahtar

    const isPickingStage = task.status.includes("PICKING");
    const isPackagingStage =
      task.status.includes("PACKAGING") || task.status === "PENDING_PACKAGING";

    const selectedStaff = await staffSelectDialog.open({
      title: isPickingStage
        ? "Toplama Sorumlusu Seç"
        : "Paketleme Sorumlusu Seç",
      data: [],
      columns: [
        {
          field: "name",
          header: "Personel Adı",
        },
        {
          field: "capabilities",
          header: "Yetkinlik & Araçlar",
          render: (r: any) => (
            <div className="flex flex-wrap gap-1">
              {r.isPicker && (
                <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 text-blue-700 font-bold border border-blue-200">
                  TOPLAYICI
                </span>
              )}
              {r.isPacker && (
                <span className="px-1.5 py-0.5 rounded text-[9px] bg-purple-100 text-purple-700 font-bold border border-purple-200">
                  PAKETLEYİCİ
                </span>
              )}
              {r.hasForklift && (
                <span className="px-1.5 py-0.5 rounded text-[9px] bg-orange-100 text-orange-700 font-bold border border-orange-200">
                  🚜 FORKLİFT
                </span>
              )}
              {r.canLiftHeavy && (
                <span className="px-1.5 py-0.5 rounded text-[9px] bg-red-100 text-red-700 font-bold border border-red-200">
                  💪 AĞIR YÜK
                </span>
              )}
            </div>
          ),
        },
      ] as any[],

      searchFields: [{ field: "name", operator: "contains" }],

      onFilter: async ({ filter, pageIndex = 1, pageSize = 10 }: any = {}) => {
        const allStaff = await taskStore.getStaffList();

        let filtered = allStaff.map((s: any) => ({
          id: s.value, // Store içindeki .id alanıyla eşleşmesi için burası önemli
          name: s.label,
          isPicker: s.isPicker,
          isPacker: s.isPacker,
          hasForklift: s.hasForklift,
          canLiftHeavy: s.canLiftHeavy,
          experienceLevel: s.experienceLevel,
        }));

        // Stage bazlı filtreleme
        if (isPickingStage) {
          filtered = filtered.filter((s: any) => s.isPicker === true);
        } else if (isPackagingStage) {
          filtered = filtered.filter((s: any) => s.isPacker === true);
        }

        // Arama filtresi
        const searchValue = filter?.value || filter?.filter?.value || (Array.isArray(filter?.filters) ? filter.filters[0]?.value : null);

        if (searchValue) {
          const searchVal = String(searchValue).toLowerCase();
          filtered = filtered.filter((s: any) =>
            s.name.toLowerCase().includes(searchVal)
          );
        }

        // 🌟 2. Sayfalamadan (slice) hemen önce listeyi son seçilenlere göre sıralıyoruz
        const sortedFiltered = getSortedList(MODULE_KEY, filtered);

        const start = (pageIndex - 1) * pageSize;
        return {
          data: sortedFiltered.slice(start, start + pageSize),
          total: sortedFiltered.length,
        };
      },
    });

    if (selectedStaff && !Array.isArray(selectedStaff)) {
      const selectedId = (selectedStaff as any).id;

      // 🌟 3. Kullanıcı personeli başarıyla seçtiğinde ID'sini store'a (ve LocalStorage'a) kaydediyoruz
      saveLastSelection(MODULE_KEY, selectedId);

      handleTaskUpdate(task.id, { assignedUserId: selectedId });
    }
  };

  const styleTag = useMemo(() => (
    <style>{`
      @container (max-width: 140px) {
        .op-kadro-container .full-label { display: none; }
        .op-kadro-container .short-label { display: inline; }
        .op-kadro-container .sorumlu-label { display: none; }
      }
      @container (min-width: 141px) {
        .op-kadro-container .full-label { display: inline; }
        .op-kadro-container .short-label { display: none; }
        .op-kadro-container .sorumlu-label { display: inline; }
      }
    `}</style>
  ), []);

  const columns = useMemo<DataTableColumn<WarehouseTask>[]>(
    () => [
      {
        field: "createdAt",
        header: "Oluşturulma",
        textOverflow: "wrap",
        align: "center",
        maxLines: 2,
        body: (r) => (
          <span className="text-xs">
            {new Date(r.createdAt).toLocaleString("tr-TR")}
          </span>
        ), // Yerel format
        sortable: true,

      },
      {
        field: "order.orderNumber",
        header: "Sipariş No",
        body: (r) => (
          <div
            className="flex flex-col min-w-0 cursor-pointer group" // min-w-max yerine min-w-0
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTask(r);
            }}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono font-bold text-blue-600 group-hover:underline break-all" title={r.order?.orderNumber}>
                {r.order?.orderNumber}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (r.order?.orderNumber) {
                    navigator.clipboard.writeText(r.order.orderNumber);
                    toast.success("Sipariş numarası kopyalandı!");
                  }
                }}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Sipariş Numarasını Kopyala"
              >
                <Copy size={14} />
              </button>
              {r.order?.source && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 shadow-sm uppercase">
                  {r.order.source}
                </span>
              )}
            </div>

            {/* İsim alanı için garanti altına alınmış truncate yapısı */}
            <span className="text-[15px] text-gray-400 block truncate max-w-full">
              {r.order?.account?.name}
            </span>

            {/* Fatura numarası alanı */}
            <span className="text-[12px] text-gray-400 block truncate max-w-full">
              {r.order?.invoiceNumber}
            </span>
          </div>
        ),

        sortable: true,
      },
      {
        field: "order.account.phone",
        header: "Telefon",
        textOverflow: "wrap",
        align: "center",
        maxLines: 3,
        body: (r) => (
          <span className="text-[12px]  ">
            {r.order?.account?.phone || "-"}
          </span>
        ),
        cellHoverActions: (r: any) => (
          <IconButton
            id={`phone-btn-${r.id}`}
            icon={Pencil}
            className="p-button-text text-primary"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTask(r);
              setTempPhone(r.order?.account?.phone || "");
              setTimeout(() => {
                const newTarget = document.getElementById(`phone-btn-${r.id}`);
                if (newTarget) phoneOp.current?.show(null, newTarget);
              }, 50);
            }}
          />
        ),
        sortable: true,
      }, {
        field: "order.shipment",
        header: "Kargo",
        sortable: true,
        body: (r) => {
          const shipmentStr = (r.order?.shipment || "").trim().toUpperCase();
          const isDepotOrPickup = shipmentStr.includes("DEPO") || shipmentStr.includes("GEL-AL") || shipmentStr.includes("GEL AL") || shipmentStr === "-" || shipmentStr === "";

          const orderItems = r.order?.items || r.items || [];
          const hasShippingItem = orderItems.some((i: any) =>
            i.stock?.name?.toLowerCase().includes("nakliye") ||
            i.stock?.sku?.toLowerCase().includes("nakliye") ||
            i.stock?.sku?.toUpperCase() === "K" ||
            i.stock?.name?.toLowerCase().includes("kargo")
          );

          return (
            <div className="flex flex-col gap-1 items-start">
              {/* Kargo Ödeme Durumu: Sadece kargo firması olan gönderilerde göster (Depo teslim / Gel-al hariç) */}
              {!isDepotOrPickup && (
                <div className="flex items-center">
                  {hasShippingItem ? (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tight bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-xs"
                      title="Siparişte nakliye/kargo kalemi (K) mevcut - Gönderici Öder"
                    >
                      <Truck size={10} className="shrink-0" />
                      Nakliye Ödendi
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tight bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 shadow-xs"
                      title="Siparişte nakliye kalemi yok - Alıcı Öder"
                    >
                      <Truck size={10} className="shrink-0" />
                      Alıcı Öder
                    </span>
                  )}
                </div>
              )}

              {/* Kargo Firması ve Kopyalama */}
              <div className="flex items-center gap-1 flex-wrap">
                <CustomCargoDropdown
                  currentCargo={r.order?.shipment || ""}
                  onSave={(val) => handleTaskUpdate(r.id, { shipment: val })}
                />
                {r.order?.shipment && r.order.shipment !== "-" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (r.order?.shipment) {
                        navigator.clipboard.writeText(String(r.order.shipment));
                        toast.success(`Kargo adı kopyalandı: ${r.order.shipment}`);
                      }
                    }}
                    className="p-1 text-gray-400 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                    title={`Kargo Adını Kopyala (${r.order.shipment})`}
                  >
                    <Copy size={12} />
                  </button>
                )}
              </div>

              {/* Kargo Takip No ve Kopyalama */}
              {r.order?.cargoTrackingNo && (() => {
                const trackingNo = String(r.order.cargoTrackingNo).trim();
                const isRealTracking = Boolean(
                  trackingNo &&
                  trackingNo !== String(r.order?.cargoIntegrationNo || '').trim() &&
                  trackingNo !== String(r.order?.orderNumber || '').trim() &&
                  trackingNo !== String(r.order?.id || '').trim()
                );

                if (!isRealTracking) {
                  return (
                    <span
                      className="text-[11px] font-medium px-2 py-0.5 rounded-md inline-block whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px] text-orange-600 bg-orange-500/10 dark:text-orange-400"
                      title={r.order?.cargoStatus ? `Durum: ${r.order.cargoStatus} (İşlem Bekliyor)` : "Kargoda Bekliyor (Henüz Takip No Oluşmadı)"}
                    >
                      Kargoda Bekliyor
                    </span>
                  );
                }

                return (
                  <div className="flex items-center gap-1 flex-wrap">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(trackingNo);
                        toast.success(`Kargo takip no kopyalandı: ${trackingNo}`);
                      }}
                      className="group text-[11px] font-medium px-2 py-0.5 rounded-md inline-flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px] cursor-pointer hover:opacity-85 transition-all border border-transparent hover:border-current text-primary bg-primary/10"
                      title={
                        r.order?.cargoStatus
                          ? `Durum: ${r.order.cargoStatus} • Kargo Takip Numarasını Kopyala`
                          : "Kargo Takip Numarasını Kopyala"
                      }
                    >
                      <span className="truncate">{trackingNo}</span>
                      <Copy size={11} className="shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </button>

                    {/* Kargo Adı + Takip No Birlikte Kopyalama Butonu (Sadece gerçek takip no varken) */}
                    {r.order?.shipment && r.order.shipment !== "-" && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const combinedText = `${r.order.shipment} - ${trackingNo}`;
                          navigator.clipboard.writeText(combinedText);
                          toast.success(`Kargo ve takip no kopyalandı: ${combinedText}`);
                        }}
                        className="p-1 text-gray-400 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                        title={`Kargo Adı ve Takip No Birlikte Kopyala (${r.order.shipment} - ${trackingNo})`}
                      >
                        <Copy size={11} className="text-primary/70" />
                      </button>
                    )}
                  </div>
                );
              })()}
              {r.order?.shipment === "YURTİÇİ" && !r.order?.shippingAddress && (
                <span
                  className="text-[10px] bg-red-500/10 text-red-600 font-bold px-1.5 py-0.5 rounded flex items-center gap-1 border border-red-500/20 w-fit"
                  title="Siparişe ait teslimat adresi bulunamadığı için kargo oluşturulamaz!"
                >
                  <AlertTriangle size={10} />
                  Adres Eksik
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: "totalDesi",
        header: "Toplam Desi",
        align: "center",
        body: (r: any) => {
          let totalDesi = 0;
          if (r.items && Array.isArray(r.items)) {
            r.items.forEach((item: any) => {
              const qty = item.quantity || 1;
              const desi = item.stock?.desi || 0;
              const desiMultiplier = item.stock?.desiMultiplier || 0;
              if (qty > 0) {
                totalDesi += desiMultiplier > 0 ? (desi + (qty - 1) * desiMultiplier) : (desi * qty);
              }
            });
          }
          return (
            <span className="font-semibold text-[13px] text-surface-700 dark:text-surface-300">
              {totalDesi > 0 ? totalDesi.toFixed(2) : "-"}
            </span>
          );
        }
      },
      {
        field: "order.description",
        header: "Sipariş Notu",

        body: (r) => (
          <p
            className="
    block
    text-sm
    text-surface-700 dark:text-surface-200
    leading-tight
    break-words
    whitespace-normal
    overflow-hidden
    [display:-webkit-box]
    [-webkit-line-clamp:5]
    [-webkit-box-orient:vertical]
  "
          >
            {r.order?.description || (
              <span className="italic text-surface-400 text-xs">
                Not yok...
              </span>
            )}
          </p>
        ),
        cellHoverActions: (r: any) => (
          <IconButton
            id={`note-btn-${r.id}`}
            icon={Pencil}
            className="p-button-text text-primary"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTask(r); // Sadece düzenleme state'ini doldur
              setTempNote(r.order?.description || "");
              setTimeout(() => {
                const newTarget = document.getElementById(`note-btn-${r.id}`);
                if (newTarget) noteOp.current?.show(null, newTarget);
              }, 50);
            }}
          />
        ),
      },
      {
        field: "order.shippingAddress",
        header: "Sevkiyat Adresi",

        body: (r) => (
          <p className="block text-sm text-surface-700 dark:text-surface-200 leading-tight break-words whitespace-normal overflow-hidden [display:-webkit-box] [-webkit-line-clamp:5] [-webkit-box-orient:vertical]">
            {r.order?.shippingAddress?.addressLine || (
              <span className="italic text-surface-400 text-xs">
                Adres girilmemiş...
              </span>
            )}
          </p>
        ),
        cellHoverActions: (r: any) => (
          <IconButton
            id={`ship-addr-btn-${r.id}`}
            icon={Pencil}
            className="p-button-text text-primary"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTask(r);
              setAddressEditType("shipping"); // Tipi belirle
              setTempAddressLine(
                r.order?.shippingAddress?.addressLine || "",
              ); // Mevcut adresi geçici hafızaya al
              setTimeout(() => {
                const newTarget = document.getElementById(`ship-addr-btn-${r.id}`);
                if (newTarget) addressOp.current?.show(null, newTarget);
              }, 50);
            }}
          />
        ),
      },


      {
        field: "order.billingAddress",
        header: "Fatura Adresi",

        body: (r) => (
          <p className="block text-sm text-surface-700 dark:text-surface-200 leading-tight break-words whitespace-normal overflow-hidden [display:-webkit-box] [-webkit-line-clamp:5] [-webkit-box-orient:vertical]">
            {r.order?.billingAddress?.addressLine || (
              <span className="italic text-surface-400 text-xs">
                Adres girilmemiş...
              </span>
            )}
          </p>
        ),
        cellHoverActions: (r: any) => (
          <IconButton
            id={`bill-addr-btn-${r.id}`}
            icon={Pencil}
            className="p-button-text text-primary"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTask(r);
              setAddressEditType("billing"); // Tipi belirle
              setTempAddressLine(
                r.order?.billingAddress?.addressLine || "",
              ); // Mevcut adresi geçici hafızaya al
              setTimeout(() => {
                const newTarget = document.getElementById(`bill-addr-btn-${r.id}`);
                if (newTarget) addressOp.current?.show(null, newTarget);
              }, 50);
            }}
          />
        ),
      },
      {
        field: "assignedUser.name",
        header: "Operasyon Kadrosu",

        body: (r: any) => {
          // WarehouseTask yerine any veya ExtendedWarehouseTask
          // Hata Çözümü: actions yoksa boş dizi kabul et
          const actions = r.actions || [];
          const pickerAction = [...actions]
            .reverse()
            .find(
              (a: any) =>
                a.actionType === "PICKING_DONE" ||
                a.actionType === "PICKING_START",
            );
          const packerAction = [...actions]
            .reverse()
            .find(
              (a: any) =>
                a.actionType === "PACKAGING_DONE" ||
                a.actionType === "PACKAGING_START",
            );

          return (
            <div className="flex flex-col gap-1 p-1 min-w-0 w-full op-kadro-container" style={{ containerType: "inline-size" }}>
              <div
                className="flex items-center justify-between card2 p-1 rounded border border-borderColor/20 cursor-pointer hover:border-primary/50 min-w-0"
                // Hata Çözümü: Fazla parametreyi ('GENERAL') kaldırdık
                onClick={() => openStaffSelector(r)}
              >
                <div className="flex items-center gap-1 overflow-hidden pr-1">
                  <span className="text-[12px] font-bold text-primary uppercase shrink-0 sorumlu-label">
                    Sorumlu:
                  </span>
                  {r.assignedUser ? (
                    <span className="text-[13px] font-semibold truncate">
                      {r.assignedUser.name}
                    </span>
                  ) : (
                    <span className="text-[13px] text-orange-500 font-bold animate-pulse shrink-0">
                      ATA
                    </span>
                  )}
                </div>
                <RefreshCw size={10} className="text-primary/50 shrink-0 ml-1" />
              </div>
              {/* 2. OPERASYONEL PERSONEL (Aksiyonlardan gelen) */}
              <div className="flex flex-col gap-1 pl-1 border-l-2 border-primary/60">
                {/* Toplayan */}
                {pickerAction && (
                  <div className="flex items-center gap-1 text-[9px] overflow-hidden">
                    <span className="text-green-600 font-bold shrink-0">
                      <span className="full-label">Toplayan:</span>
                      <span className="short-label">T:</span>
                    </span>
                    <span className="text-text/70 truncate">
                      {pickerAction.user?.name}
                    </span>
                  </div>
                )}
                {/* Paketleyen */}
                {packerAction && (
                  <div className="flex items-center gap-1 text-[9px] overflow-hidden">
                    <span className="text-blue-600 font-bold shrink-0">
                      <span className="full-label">Paketleyen:</span>
                      <span className="short-label">P:</span>
                    </span>
                    <span className="text-text/70 truncate">
                      {packerAction.user?.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        field: "priority",
        header: "Öncelik",

        body: (r) => (
          <Dropdown
            value={r.priority}
            options={[
              { label: "Düşük", value: 1 },
              { label: "Normal", value: 2 },
              { label: "Yüksek", value: 3 },
              { label: "Acil", value: 4 },
              { label: "Kritik", value: 5 },
            ]}
            optionValue="value"
            onChange={(e) => handleTaskUpdate(r.id, { priority: e.value })}
            className={`w-full text-xs font-bold ${r.priority >= 4 ? "p-invalid" : ""}`}
          />
        ),
      },
      {
        field: "order.totalAmount",
        header: "Toplam Tutar",
        sortable: true,

        body(rowData, options) {
          const amount = rowData.order?.totalAmount || 0;
          return (
            <span className="font-semibold text-sm">
              {amount.toLocaleString("tr-TR", {
                style: "currency",
                currency: "TRY",
              })}
            </span>
          );
        },
      },
      {
        field: "status",
        header: "Durum",

        body: (r) => {
          return (
            <CustomStatusDropdown
              currentStatus={r.status}
              disabled={r.status === "COMPLETED" || r.status === "CANCELLED"}
              onSave={async (newStatus) => {
                try {
                  await taskStore.adminUpdate(r.id, {
                    status: newStatus
                  });
                  toast.success("Görev durumu başarıyla güncellendi.");
                  // Refresh the list is handled by the store
                } catch (error: any) {
                  toast.error(error.response?.data?.message || "Durum güncellenirken bir hata oluştu.");
                }
              }}
            />
          );
        },
        sortable: true,
      },
      {
        field: "actions",
        header: "İşlemler",
        sortable: false,
        width: "220px",
        align: "center",
        body: (r) => (
          <TableActions
            actions={[
              {
                icon: r.isAssignmentRequired ? Lock : Unlock,
                colorClass: r.isAssignmentRequired ? "text-red-500 hover:bg-red-50" : "text-emerald-500 hover:bg-emerald-50",
                title: r.isAssignmentRequired ? "Havuz Kilidini Kaldır" : "Havuz Kilidi Koy",
                onClick: () => handleTaskUpdate(r.id, { isAssignmentRequired: !r.isAssignmentRequired })
              },
              {
                icon: Eye,
                colorClass: "text-blue-500 hover:bg-blue-50",
                title: "Detay Göster",
                onClick: () => setSelectedTask(r)
              },
              {
                icon: CheckCircle2,
                colorClass: "text-emerald-500 hover:bg-emerald-50",
                title: "Hızlı Tamamla",
                onClick: () => handleFinishTask(r),
                show: r.status !== "COMPLETED" && r.status !== "CANCELLED"
              },
              {
                icon: Undo2,
                colorClass: "text-orange-500 hover:bg-orange-50",
                title: "Görevi Sıfırla",
                onClick: () => handleResetTask(r)
              },
              {
                icon: Trash2,
                colorClass: "text-red-600 hover:bg-red-50",
                title: "Görevi Sil",
                onClick: () => handleDeleteTask(r)
              },
              {
                icon: Share2,
                colorClass: "text-blue-600 hover:bg-blue-50",
                title: "Kargo Bilgisini Kopyala",
                onClick: () => handleCopyCargoInfo(r)
              },
              {
                icon: PrinterIcon,
                colorClass: "text-blue-600 hover:bg-blue-50",
                title: "Toplayıcı Etiketi Yazdır",
                onClick: () => {
                  setTaskToPrint(r);
                  setPrintCategory("PICKING");
                  setPrintDialogOpen(true);
                }
              },
              {
                icon: PrinterIcon,
                colorClass: "text-purple-600 hover:bg-purple-50",
                title: "Paketleyici Etiketi Yazdır",
                onClick: () => {
                  setTaskToPrint(r);
                  setPrintCategory("PACKING");
                  setPrintDialogOpen(true);
                }
              }
            ]}
          />
        ),
      },
    ],
    [handleTaskUpdate],
  );
  const isBatchAction = selectedTasks.length > 0;

  const handleBulkComplete = async () => {
    const ids = selectedTasks.map((t) => t.id);

    const confirmMessage = isBatchAction
      ? `Seçilen ${ids.length} adet görevi 'Tamamlandı' statüsüne çekmek istediğinize emin misiniz?`
      : "Şu anda bekleyen veya operasyonda olan TÜM depo görevlerini 'Tamamlandı' statüsüne çekmek istediğinize emin misiniz? Bu işlem toplu bir onaydır.";

    const confirmTitle = isBatchAction
      ? "Seçili Görevleri Tamamla"
      : "Tümünü Tamamla - Kritik Onay";

    const confirmed = await confirmModal.confirm(confirmMessage, confirmTitle);

    if (confirmed) {
      const loadToast = toast.loading("İşlem yapılıyor...");
      try {
        // Seçim varsa id'leri, yoksa undefined göndererek backend'in tamamını yapmasını sağlıyoruz
        await taskStore.completeBulk(isBatchAction ? ids : undefined);

        toast.update(loadToast, {
          render: `${isBatchAction ? ids.length + " seçili görev" : "Tüm aktif görevler"} başarıyla tamamlandı.`,
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        refreshAll();
        if (isBatchAction) setSelectedTasks([]); // Seçim varsa işlemi bitince temizle
      } catch (e) {
        toast.update(loadToast, {
          render: "İşlem sırasında bir hata oluştu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };
  const handleCompleteAll = async () => {
    const confirmed = await confirmModal.confirm(
      "Şu anda bekleyen veya operasyonda olan TÜM depo görevlerini 'Tamamlandı' statüsüne çekmek istediğinize emin misiniz? Bu işlem toplu bir onaydır.",
      "Tümünü Tamamla - Kritik Onay",
    );

    if (confirmed) {
      const loadToast = toast.loading("Tüm görevler tamamlanıyor...");
      try {
        await taskStore.completeAll();
        toast.update(loadToast, {
          render: "Tüm aktif görevler başarıyla tamamlandı.",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        refreshAll(); // Hem özetleri hem tabloyu yenile
      } catch (e) {
        toast.update(loadToast, {
          render: "Toplu işlem sırasında bir hata oluştu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };
  return (
    <div className="w-full h-full flex flex-col p-0 animate-in fade-in duration-500">
      {styleTag}
      {/* Özet Kartları
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <SummaryCard title="İş Havuzu" value={summary.POOL} icon={<Clock size={18} />} color="card2 border-l-4 border-l-orange-500" />
                <SummaryCard title="Personelde" value={summary.ASSIGNED} icon={<UserCheck size={18} />} color="card2 border-l-4 border-l-blue-500" />
                <SummaryCard title="Operasyonda" value={summary.PROGRESS} icon={<Activity size={18} />} color="card2 border-l-4 border-l-cyan-500" />
                <SummaryCard title="Kritik Eksik" value={summary.SHORTAGE} icon={<AlertTriangle size={18} />} color="card2 border-l-4 border-l-red-500" />
                <SummaryCard title="Biten Bugün" value={summary.COMPLETED} icon={<CheckCircle2 size={18} />} color="card2 border-l-4 border-l-green-500" />
                <SummaryCard title="İptaller" value={summary.CANCELLED} icon={<Trash2 size={18} />} color="card2 border-l-4 border-l-gray-500" />
            </div> */}

      {/* Ana Tablo 
      <div className="card p-0 shadow-lg border-0">*/}
      <EDataTable<WarehouseTask>
        tableKey="warehouse_operation_monitor_v2"
        placeholder="Sipariş No veya Müşteri Ara..."
        useStore={() => taskStore}
        dataKey="id"
        scrollh={350}
        title="Operasyon Monitörü"
        filters={extraFilters}
        defaultFilterExpanded={true}
        showSelection={true}
        showIndex={true}
        selection={selectedTasks}
        onSelectionChange={(e) => setSelectedTasks(e.value)}
        virtualize={true}
        actions={
          <div className="flex gap-2">

            {selectedTasks.length > 0 && (
              <IconTextButton
                onClick={() => setSelectedTasks([])}
                icon={X}
                className="text-text hover:text-red-500 bg-inputBg border-inputBorder hover:border-red-500/50 hover:bg-red-500/10 transition-all shadow-sm w-full lg:w-auto items-center justify-center"
              >
                Sıfırla
              </IconTextButton>
            )}
            <IconTextButton
              onClick={handleBulkComplete}
              icon={CheckCheck}
              disabled={!isBatchAction}
              className={`text-white transition-all w-full lg:w-auto items-center justify-center ${isBatchAction
                ? "bg-green-600 hover:bg-green-700 shadow-md"
                : "bg-gray-400 hover:bg-gray-700 cursor-not-allowed opacity-60"
                }`}
            >
              {isBatchAction
                ? `${selectedTasks.length} Tamamla`
                : `${selectedTasks.length} Tamamla`}
            </IconTextButton>
          </div>
        }
        filterBar={
          <div className="w-full card2 dark:bg-surface-900 border border-borderColor/30 rounded-2xl shadow-sm p-1 flex flex-col xl:flex-row items-stretch xl:items-center gap-1.5 transition-all">
            {/* 1. Sol Taraf: Zaman Filtresi (Mobilde tam genişlik, masaüstünde kendi kadar) */}
            <div className="w-full xl:w-auto flex-shrink-0">
              <DateRangePicker
                value={filterDates}
                onChange={setFilterDates}
              />
            </div>

            {/* Masaüstü Ayırıcı Çizgi */}
            <div className="hidden xl:block w-[1px] h-6 bg-borderColor/20 mx-1" />

            {/* 2. Sağ Taraf: Süreç Filtreleri (Tablette wrap olur, Masaüstünde tek satır kalır) */}
            <div className="flex flex-col sm:flex-row flex-wrap xl:flex-nowrap flex-1 gap-1.5 min-w-0">
              {/* Durum Filtresi */}
              <MultiSelect
                value={filterStatuses}
                options={[
                  { label: "Toplama Bekliyor", value: "PENDING_PICKING" },
                  { label: "Toplama Devam Ediyor", value: "PICKING" },
                  { label: "Toplama Duraklatıldı", value: "PICKING_PAUSED" },
                  {
                    label: "Toplama Bitti (Paketleme Bekliyor)",
                    value: "PENDING_PACKAGING",
                  },
                  { label: "Paketleme Devam Ediyor", value: "PACKAGING" },
                  {
                    label: "Paketleme Duraklatıldı",
                    value: "PACKAGING_PAUSED",
                  },
                  { label: "Eksik Ürün Bildirimi", value: "SHORTAGE" },
                  {
                    label: "Tedarik Bekleniyor",
                    value: "WAITING_FOR_SUPPLY",
                  },
                  { label: "Revizyon Gerekiyor", value: "REVISION_NEEDED" },
                  { label: "Tamamlandı", value: "COMPLETED" },
                  { label: "İptal Edildi", value: "CANCELLED" },
                ]}
                onChange={(e) => setFilterStatuses(e.value)}
                placeholder="Durum Seçin"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} Durum"
                display="chip"
                className="flex-1 sm:min-w-[150px] xl:w-40 h-9 !border-transparent hover:!border-borderColor/30 !bg-gray-50/50 dark:!bg-surface-800 rounded-xl transition-all text-[11px] flex items-center"
              />

              {/* Atanan Personel Filtresi */}
              <MultiSelect
                value={filterAssignedUsers}
                options={staffOptions}
                onChange={(e) => setFilterAssignedUsers(e.value)}
                placeholder="Personel"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} Personel"
                display="chip"
                filter
                className="flex-1 sm:min-w-[140px] xl:w-36 h-9 !border-transparent hover:!border-borderColor/30 !bg-gray-50/50 dark:!bg-surface-800 rounded-xl transition-all text-[11px] flex items-center"
              />

              {/* Sipariş Kaynağı Filtresi */}
              <MultiSelect
                value={filterSources}
                options={sourceOptions}
                onChange={(e) => setFilterSources(e.value)}
                placeholder="Kaynak"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} Kaynak"
                display="chip"
                filter
                className="flex-1 sm:min-w-[130px] xl:w-36 h-9 !border-transparent hover:!border-borderColor/30 !bg-gray-50/50 dark:!bg-surface-800 rounded-xl transition-all text-[11px] flex items-center"
              />

              {/* Atadıklarım Aç/Kapa Butonu */}
              <button
                type="button"
                onClick={() => setFilterMyAssignedOnly((prev) => !prev)}
                className={`flex-shrink-0 h-9 px-3 rounded-xl font-bold transition-all text-[11px] flex items-center justify-center gap-1.5 border shadow-sm select-none ${
                  filterMyAssignedOnly
                    ? "bg-primary text-white border-primary shadow-primary/20 hover:bg-primaryHover"
                    : "bg-gray-50/50 dark:bg-surface-800 text-text/70 hover:text-text border-transparent hover:border-borderColor/30"
                }`}
                title={filterMyAssignedOnly ? "Tüm görevleri göster" : "Sadece atadığım veya bana atanan görevleri göster"}
              >
                <UserCheck size={14} className={filterMyAssignedOnly ? "text-white" : "text-primary"} />
                <span>Atadıklarım</span>
                {filterMyAssignedOnly && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse ml-0.5" />
                )}
              </button>
            </div>

            {/* 3. Tümünü Temizleme Butonu */}
            {(filterStatuses.length > 0 ||
              filterAssignedUsers.length > 0 ||
              filterSources.length > 0 ||
              filterMyAssignedOnly ||
              filterPickers.length > 0 ||
              filterPackers.length > 0 ||
              filterDates) && (
                <>
                  <div className="hidden xl:block w-[1px] h-6 bg-borderColor/20 mx-1" />
                  <button
                    onClick={() => {
                      setFilterStatuses([]);
                      setFilterAssignedUsers([]);
                      setFilterSources([]);
                      setFilterMyAssignedOnly(false);
                      setFilterPickers([]);
                      setFilterPackers([]);
                      setFilterDates(null);
                    }}
                    className="flex-shrink-0 w-full xl:w-auto px-4 h-9 rounded-xl bg-red-200 hover:bg-red-100 text-red-600 font-bold transition-all text-[11px] flex items-center justify-center gap-1.5"
                    title="Filtreleri Temizle"
                  >
                    <Trash2 size={14} />
                    <span className="xl:hidden 2xl:inline">Temizle</span>
                  </button>
                </>
              )}
          </div>
        }
        columns={columns}
        searchFields={[
          { field: "order.orderNumber", operator: "contains" },
          { field: "order.invoiceNumber", operator: "contains" },
          { field: "order.account.name", operator: "contains" },
          { field: "order.account.phone", operator: "contains" },
          { field: "order.shipment", operator: "contains" },
          { field: "order.shippingAddress.addressLine", operator: "contains" },
          { field: "order.source", operator: "contains" },
        ]}
      />
      {/* 🌟 Filtre Overlay Paneli */}
      <OverlayPanel
        ref={filterOp}
        appendTo={typeof window !== 'undefined' ? document.body : null}
        className="shadow-2xl border-0 overflow-hidden card2 p-0"
        style={{ width: "350px", borderRadius: "16px" }}
      >
        <div className="flex flex-col gap-4 p-4">
          {/* Header */}
          <div className="flex justify-between items-center border-b border-borderColor/30 pb-3">
            <div className="flex items-center gap-2 text-primary">
              <FilterIcon size={18} />
              <span className="font-bold tracking-tight text-text">
                Gelişmiş Filtrele
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold opacity-50 uppercase">
                Tarih Aralığı
              </label>
              <Calendar
                value={filterDates as any} // Tip uyuşmazlığını aşmak için veya Nullable dökümü için
                onChange={(e) => setFilterDates(e.value as (Date | null)[])}
                selectionMode="range"
                readOnlyInput
                placeholder="Tarih Aralığı Seçin"
                className="w-full"
                showIcon
              />
            </div>
            <button
              className="text-[10px] font-black text-danger hover:bg-danger/10 px-2 py-1 rounded-lg uppercase tracking-tighter transition-all"
              onClick={() => {
                setFilterStatuses([]);
                setFilterPriorities([]);
                setFilterAssignedUsers([]);
                setFilterUsers([]);
                setFilterSources([]);
                setFilterMyAssignedOnly(false);
                setFilterDates(null);
              }}
            >
              TÜMÜNÜ SIFIRLA
            </button>
          </div>

          {/* Content */}
          <div className="space-y-5 py-2 overflow-y-auto max-h-[450px]">
            {/* 1. Görev Durumları (Full Liste) */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-text opacity-40 uppercase tracking-widest px-1">
                Görev Durumu
              </label>
              <MultiSelect
                value={filterStatuses}
                options={[
                  { label: "Toplama Bekliyor", value: "PENDING_PICKING" },
                  { label: "Toplama Devam Ediyor", value: "PICKING" },
                  { label: "Toplama Duraklatıldı", value: "PICKING_PAUSED" },
                  {
                    label: "Toplama Bitti (Paketleme Bekliyor)",
                    value: "PENDING_PACKAGING",
                  },
                  { label: "Paketleme Devam Ediyor", value: "PACKAGING" },
                  {
                    label: "Paketleme Duraklatıldı",
                    value: "PACKAGING_PAUSED",
                  },
                  { label: "Eksik Ürün Bildirimi", value: "SHORTAGE" },
                  { label: "Tedarik Bekleniyor", value: "WAITING_FOR_SUPPLY" },
                  { label: "Revizyon Gerekiyor", value: "REVISION_NEEDED" },
                  { label: "Tamamlandı", value: "COMPLETED" },
                  { label: "İptal Edildi", value: "CANCELLED" },
                ]}
                onChange={(e) => setFilterStatuses(e.value)}
                placeholder="Durum Seçin"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} adet seçildi"
                display="chip"
                className="w-full card border-borderColor/50"
              />
            </div>

            {/* 2. Personel Filtresi (Dinamik) */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-text opacity-40 uppercase tracking-widest px-1">
                Sorumlu Personel
              </label>
              <MultiSelect
                value={filterAssignedUsers}
                options={staffOptions}
                optionLabel="label"
                optionValue="value"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} adet seçildi"
                onChange={(e) => setFilterAssignedUsers(e.value)}
                placeholder="Personel Seçin"
                display="chip"
                filter // Personel çoksa içinde arama yapabilmek için
                className="w-full card border-borderColor/50"
              />
            </div>

            {/* 3. Öncelik Filtresi */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-text opacity-40 uppercase tracking-widest px-1">
                Öncelik Seviyesi
              </label>
              <MultiSelect
                value={filterPriorities}
                options={[
                  { label: "Düşük (1)", value: 1 },
                  { label: "Normal (2)", value: 2 },
                  { label: "Yüksek (3)", value: 3 },
                  { label: "Acil (4)", value: 4 },
                  { label: "Kritik (5)", value: 5 },
                ]}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} adet seçildi"
                onChange={(e) => setFilterPriorities(e.value)}
                placeholder="Öncelik Seçin"
                display="chip"
                className="w-full card border-borderColor/50"
              />
            </div>

            {/* 4. Sipariş Kaynağı Filtresi */}
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-text opacity-40 uppercase tracking-widest px-1">
                Sipariş Kaynağı
              </label>
              <MultiSelect
                value={filterSources}
                options={sourceOptions}
                maxSelectedLabels={1}
                selectedItemsLabel="{0} adet seçildi"
                onChange={(e) => setFilterSources(e.value)}
                placeholder="Kaynak Seçin"
                display="chip"
                filter
                className="w-full card border-borderColor/50"
              />
            </div>

            {/* 5. Sadece Atadıklarım Toggle */}
            <div className="flex items-center justify-between p-2 rounded-xl bg-gray-50/50 dark:bg-surface-800 border border-borderColor/30">
              <div className="flex items-center gap-2">
                <UserCheck size={16} className="text-primary" />
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-text">Sadece Atadıklarım</span>
                  <span className="text-[10px] text-text/50">Atadığınız veya size atanan işler</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFilterMyAssignedOnly((prev) => !prev)}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                  filterMyAssignedOnly
                    ? "bg-primary text-white shadow-sm"
                    : "bg-card text-text/70 border border-borderColor/40 hover:text-text"
                }`}
              >
                {filterMyAssignedOnly ? "Aktif" : "Pasif"}
              </button>
            </div>
          </div>

          {/* Footer Alt Bilgi */}
          <div className="pt-2 border-t border-borderColor/20 flex justify-center">
            <span className="text-[9px] font-bold opacity-30 uppercase tracking-widest text-text">
              {extraFilters.length} aktif filtre uygulanıyor
            </span>
          </div>
        </div>
      </OverlayPanel>

      {/* Not Düzenleme Paneli */}
      <OverlayPanel
        ref={noteOp}
        appendTo={typeof window !== 'undefined' ? document.body : null}
        onHide={() => setEditingTask(null)}
        className="shadow-2xl border border-borderColor overflow-hidden bg-card p-0"
        style={{ width: "350px", borderRadius: "1rem" }}
      >
        <div className="flex flex-col gap-3 p-4 bg-card">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-text">
              Sipariş Notu Düzenle
            </span>
            <Pencil size={14} className="text-primary" />
          </div>

          <InputTextarea
            value={tempNote}
            onChange={(e) => setTempNote(e.target.value)}
            rows={3}
            autoResize
            placeholder="Sipariş için not giriniz..."
            className="w-full p-3 text-sm bg-inputBg border border-inputBorder rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-inputText placeholder:text-text/30 transition-all"
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === "Enter" && editingTask) {
                handleTaskUpdate(editingTask.id, { description: tempNote });
                noteOp.current?.hide();
              }
            }}
            autoFocus
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => noteOp.current?.hide()}
              className="px-4 py-2 rounded-xl text-sm font-bold text-text/60 hover:bg-bgAlt hover:text-text transition-all"
            >
              İptal
            </button>
            <button
              onClick={() => {
                if (editingTask) {
                  handleTaskUpdate(editingTask.id, { description: tempNote });
                  noteOp.current?.hide();
                }
              }}
              className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/20"
            >
              Kaydet
            </button>
          </div>
        </div>
      </OverlayPanel>

      {/* Telefon Düzenleme Paneli */}
      <OverlayPanel
        ref={phoneOp}
        appendTo={typeof window !== 'undefined' ? document.body : null}
        onHide={() => {
          setEditingTask(null);
        }}
        className="shadow-2xl border border-borderColor overflow-hidden bg-card p-0"
        style={{ width: "300px", borderRadius: "1rem" }}
      >
        <div className="flex flex-col gap-3 p-4 bg-card">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-text">Telefon Düzenle</span>
            <Phone size={14} className="text-primary" />
          </div>

          <InputText
            value={tempPhone}
            onChange={(e) => setTempPhone(e.target.value)}
            placeholder="Telefon numarası..."
            className="w-full p-3 text-sm bg-inputBg border border-inputBorder rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-inputText placeholder:text-text/30 transition-all"
            onKeyDown={(e) => {
              if (e.key === "Enter" && editingTask) {
                handleTaskUpdate(editingTask.id, {
                  accountUpdate: {
                    phone: tempPhone,
                  },
                });
                phoneOp.current?.hide();
              }
            }}
            autoFocus
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => phoneOp.current?.hide()}
              className="px-4 py-2 rounded-xl text-sm font-bold text-text/60 hover:bg-bgAlt hover:text-text transition-all"
            >
              İptal
            </button>
            <button
              onClick={() => {
                if (editingTask) {
                  handleTaskUpdate(editingTask.id, {
                    accountUpdate: {
                      phone: tempPhone,
                    },
                  });
                  phoneOp.current?.hide();
                }
              }}
              className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/20"
            >
              Kaydet
            </button>
          </div>
        </div>
      </OverlayPanel>

      {/* Adres Düzenleme Paneli */}
      <OverlayPanel
        ref={addressOp}
        appendTo={typeof window !== 'undefined' ? document.body : null}
        onHide={() => {
          setEditingTask(null);
          setAddressEditType(null); // Panel kapandığında adres modunu sıfırla
        }}
        className="shadow-2xl border border-borderColor overflow-hidden bg-card p-0"
        style={{ width: "350px", borderRadius: "1rem" }}
      >
        <div className="flex flex-col gap-3 p-4 bg-card">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-text">
              {addressEditType === "shipping"
                ? "Sevkiyat Adresi Düzenle"
                : addressEditType === "billing"
                  ? "Fatura Adresi Düzenle"
                  : "Adres Düzenle"}
            </span>
            <Pencil size={14} className="text-primary" />
          </div>

          <InputTextarea
            value={tempAddressLine}
            onChange={(e) => setTempAddressLine(e.target.value)}
            rows={3} // Başlangıç yüksekliği
            autoResize // İçerik arttıkça otomatik uzar
            placeholder="Açık adres giriniz..."
            className="w-full p-3 text-sm bg-inputBg border border-inputBorder rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-inputText placeholder:text-text/30 transition-all"
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === "Enter" && editingTask && addressEditType) {
                // Hangi adres düzenleniyorsa o alana göre update çağrısı at
                if (addressEditType === "shipping") {
                  handleTaskUpdate(editingTask.id, {
                    shippingAddressUpdate: {
                      addressLine: tempAddressLine,
                    },
                  });
                } else if (addressEditType === "billing") {
                  handleTaskUpdate(editingTask.id, {
                    billingAddressUpdate: {
                      addressLine: tempAddressLine,
                    },
                  });
                }
                addressOp.current?.hide();
              }
            }}
            autoFocus
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => addressOp.current?.hide()}
              className="px-4 py-2 rounded-xl text-sm font-bold text-text/60 hover:bg-bgAlt hover:text-text transition-all"
            >
              İptal
            </button>
            <button
              onClick={() => {
                if (editingTask && addressEditType) {
                  if (addressEditType === "shipping") {
                    handleTaskUpdate(editingTask.id, {
                      shippingAddressUpdate: {
                        addressLine: tempAddressLine,
                      },
                    });
                  } else if (addressEditType === "billing") {
                    handleTaskUpdate(editingTask.id, {
                      billingAddressUpdate: {
                        addressLine: tempAddressLine,
                      },
                    });
                  }
                  addressOp.current?.hide();
                }
              }}
              className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-md shadow-primary/20"
            >
              Kaydet
            </button>
          </div>
        </div>
      </OverlayPanel>
      {/* Görev Detay Dialog'u */}
      <WarehouseTaskDetailDialog
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
      />

      {/* 🌟 Etiket Yazdırma Dialogu */}
      <WarehouseTaskPrintDialog
        isOpen={printDialogOpen}
        onClose={() => {
          setPrintDialogOpen(false);
          setTaskToPrint(null);
        }}
        task={taskToPrint}
        printCategory={printCategory}
      />
      <ManualOrderSyncModal
        isOpen={isManualSyncOpen}
        onClose={() => setIsManualSyncOpen(false)}
        onSuccess={() => {
          refreshAll();
        }}
      />
      {confirmModal.modal}
      {staffSelectDialog.modal}
    </div>
  );
};
